import _extends from "@babel/runtime/helpers/extends";
import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import React, { Component } from "react";
import { array, bool, func, object, string } from "prop-types";
import { connect } from "react-redux";
import { Alert } from "@icg360/ui-toolkit";
import { createModelProvider, rulesetsSelector, createMemoDeriveForm } from "@icg360/rex";
import { APP_IDS, PRELOAD, LOADING, LOADED, LOAD_ERROR, RULESET_OUTCOMES } from "../constants";
import { pacDataPropTypes } from "../shared/propTypes";
import Edit from "./Edit/Edit";
import Preview from "./Preview/Preview";
import Saved from "./Saved/Saved";
import FormProgressBar from "./FormProgressBar";
import { flattenInputs, parseTransactionRequestError } from "../shared/utils";
import postTransactionRequest from "../promises/postTransactionRequest";
import fusions from "../shared/fusions";
var BLOCK = RULESET_OUTCOMES.BLOCK,
    PREVENT = RULESET_OUTCOMES.PREVENT;
var deriveForm = createMemoDeriveForm();
var initialStateTransactionRequest = {
  error: undefined,
  hasRateValidationBlock: false,
  loadState: PRELOAD,
  cachedResponse: undefined,
  cachedRequestHeaders: undefined,
  cachedRequestBody: undefined,
  saved: false
};

var Form = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Form, _Component);

  function Form(props) {
    var _this;

    _this = _Component.call(this, props) || this;

    _this.handleFormSubmit = function (e) {
      e.preventDefault();
      var _this$props = _this.props,
          getRexValues = _this$props.getRexValues,
          pacData = _this$props.pacData,
          _this$props$pacData = _this$props.pacData,
          applicationId = _this$props$pacData.applicationId,
          pacId = _this$props$pacData.pacId,
          rexRulesets = _this$props.rexRulesets;
      var _this$state = _this.state,
          formFields = _this$state.formFields,
          prevTransactionRequestState = _this$state.transactionRequest;
      var isBeingSaved = prevTransactionRequestState.cachedResponse; // Scroll to top of the component on submit

      document.getElementById(pacId).scrollIntoView(); // rexFormValues = { values, changed } from createModelProvider (Rex)

      var rexFormValues = getRexValues(); // Update the required-ness of the attachment based on some dynamic values
      // that the product will be setting in the form, as well as the context
      // in which Pac is running.

      _this.updateFormField("attachment", {
        isRequired: rexFormValues.values.RequireDocumentUpload === "100" && applicationId === APP_IDS.AGENT_PORTAL
      }); // Start the loading...


      _this.setState({
        transactionRequest: _extends({}, prevTransactionRequestState, {
          loadState: LOADING
        })
      }); // Make the request...


      postTransactionRequest({
        transactionRequest: prevTransactionRequestState,
        pacData: pacData,
        staticFormValues: Object.keys(formFields).reduce(function (acc, key) {
          var _extends2;

          return _extends({}, acc, (_extends2 = {}, _extends2[key] = formFields[key].value, _extends2));
        }, {}),
        rexFormChangedValues: rexFormValues.changed,
        rexRulesets: rexRulesets
      }).then(function (_ref) {
        var response = _ref.response,
            requestBody = _ref.requestBody,
            requestHeaders = _ref.requestHeaders;

        _this.setState( // First animate the <FormProgressBar /> to complete
        {
          animateProgressBarToComplete: true
        }, // After the animation, set the state based on the request result
        postAnimationFn(function () {
          // We set different pieces of the state, depending on whether the
          // action was "previewing" or "saving"
          _this.setState({
            animateProgressBarToComplete: false,
            transactionRequest: _extends({}, prevTransactionRequestState, {
              error: undefined,
              loadState: LOADED
            }, isBeingSaved ? // Saving the thing succeeded
            {
              saved: true
            } : // Getting a preview succeeded
            {
              cachedRequestBody: requestBody,
              cachedRequestHeaders: requestHeaders,
              cachedResponse: response
            })
          });
        }));
      }).catch(function (err) {
        _this.setState( // First animate the <FormProgressBar /> to complete
        {
          animateProgressBarToComplete: true
        }, // After the animation, set the state based on the request result
        postAnimationFn(function () {
          // eslint-disable-next-line no-console
          console.error("Error posting the transaction request.", err);
          var res = err.res,
              resText = err.resText;

          _this.setState({
            animateProgressBarToComplete: false,
            transactionRequest: _extends({}, prevTransactionRequestState, {
              loadState: LOAD_ERROR
            }, isBeingSaved ? // Saving the thing failed
            {
              error: parseTransactionRequestError(res, resText)
            } : // Getting a preview failed
            {
              hasRateValidationBlock: res ? res.headers.get("rate-validation-failed") === "true" : false,
              error: parseTransactionRequestError(res, resText)
            })
          });
        }));
      });
    };

    _this.updateFormField = function (name, values) {
      var _extends3;

      _this.setState({
        formFields: _extends({}, _this.state.formFields, (_extends3 = {}, _extends3[name] = _extends({}, _this.state.formFields[name], values), _extends3))
      });
    };

    _this.backToEditFormView = function () {
      _this.setState({
        animateProgressBarToComplete: false,
        transactionRequest: initialStateTransactionRequest
      });
    };

    var _props$pacData = props.pacData,
        _applicationId = _props$pacData.applicationId,
        policyActionId = _props$pacData.policyActionId;
    _this.state = {
      animateProgressBarToComplete: false,
      transactionRequest: initialStateTransactionRequest,
      formFields: {
        effectiveDate: {
          value: "",
          // This is required for every form except `changeeffectivedate`, as
          // we have to handle that form in a special manner
          isRequired: policyActionId !== "changeeffectivedate" && policyActionId !== "renewalpreview",
          isTouched: false,
          isValid: false
        },
        comment: {
          value: "",
          isRequired: false,
          isTouched: false,
          isValid: true
        },
        attachment: {
          // will be a `File` object if set
          value: "",
          // could change based on a value coming from the form on submit
          isRequired: false,
          isTouched: false,
          isValid: true
        },
        note: {
          value: "",
          // Required for Agent Portal users
          isRequired: _applicationId === APP_IDS.AGENT_PORTAL,
          isTouched: false,
          isValid: true // always true as any content is valid

        }
      }
    };
    return _this;
  }
  /**
   * FYI: we validate the the values of each form in <Edit /> or <Preview />
   * so if this is submitting, we can assume it's valid.
   */


  var _proto = Form.prototype;

  _proto.render = function render() {
    var _this$props2 = this.props,
        pacData = _this$props2.pacData,
        _this$props2$pacData = _this$props2.pacData,
        pacId = _this$props2$pacData.pacId,
        policyActionLabel = _this$props2$pacData.policyActionLabel,
        derivedForm = _this$props2.derivedForm,
        rexFormHasNoChanges = _this$props2.rexFormHasNoChanges,
        rexFormIsInvalid = _this$props2.rexFormIsInvalid,
        rexRulesets = _this$props2.rexRulesets,
        rexRulesetsSayBlock = _this$props2.rexRulesetsSayBlock,
        rexRulesetsSayPrevent = _this$props2.rexRulesetsSayPrevent,
        columnData = _this$props2.columnData,
        columnDataTitle = _this$props2.columnDataTitle;
    var _this$state2 = this.state,
        animateProgressBarToComplete = _this$state2.animateProgressBarToComplete,
        _this$state2$transact = _this$state2.transactionRequest,
        cachedRequestBody = _this$state2$transact.cachedRequestBody,
        cachedResponse = _this$state2$transact.cachedResponse,
        error = _this$state2$transact.error,
        hasRateValidationBlock = _this$state2$transact.hasRateValidationBlock,
        loadState = _this$state2$transact.loadState,
        saved = _this$state2$transact.saved,
        _this$state2$formFiel = _this$state2.formFields,
        attachment = _this$state2$formFiel.attachment,
        comment = _this$state2$formFiel.comment,
        effectiveDate = _this$state2$formFiel.effectiveDate,
        note = _this$state2$formFiel.note;
    var isEdit = !cachedRequestBody && !saved;
    var isPreview = cachedRequestBody && !saved;
    var isSaved = cachedRequestBody && saved;
    return /*#__PURE__*/React.createElement("div", {
      id: pacId
    }, /*#__PURE__*/React.createElement("h4", {
      style: {
        marginTop: 0
      }
    }, policyActionLabel), /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement("form", {
      onSubmit: this.handleFormSubmit,
      className: "form-horizontal",
      style: {
        position: "relative"
      }
    }, loadState === LOADING && /*#__PURE__*/React.createElement(FormProgressBar, {
      animateProgressBarToComplete: animateProgressBarToComplete,
      isEdit: isEdit,
      pacId: pacId
    }), loadState !== LOADING && /*#__PURE__*/React.createElement("div", null, error && !hasRateValidationBlock &&
    /*#__PURE__*/

    /* If it was a rate validation block error, we'll show our own
       message in the <Edit /> component. Otherwise, we show our
       parsed version of the the message from the server. */
    React.createElement(Alert, {
      bsStyle: "danger"
    }, /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, error.title, ": "), error.description), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: "10px",
        marginBottom: "-10px"
      },
      dangerouslySetInnerHTML: {
        __html: error.html
      }
    })), isEdit && /*#__PURE__*/React.createElement(Edit, {
      derivedForm: derivedForm,
      backToEditFormView: this.backToEditFormView,
      comment: comment,
      effectiveDate: effectiveDate,
      hasRateValidationBlock: hasRateValidationBlock,
      pacData: pacData,
      rexFormHasNoChanges: rexFormHasNoChanges,
      rexFormIsInvalid: rexFormIsInvalid,
      rulesets: rexRulesets,
      rulesetsSayBlock: rexRulesetsSayBlock,
      rulesetsSayPrevent: rexRulesetsSayPrevent,
      updateFormField: this.updateFormField,
      columnData: columnData,
      columnDataTitle: columnDataTitle
    }), isPreview && /*#__PURE__*/React.createElement(Preview, {
      attachment: attachment,
      backToEditFormView: this.backToEditFormView,
      comment: comment,
      derivedForm: derivedForm,
      effectiveDate: effectiveDate,
      pacData: pacData,
      note: note,
      proposedPolicyData: cachedResponse.policyData,
      updateFormField: this.updateFormField,
      rulesetResults: cachedResponse.rulesets
    }), isSaved && /*#__PURE__*/React.createElement(Saved, {
      attachment: attachment,
      note: note,
      pacData: pacData,
      derivedForm: derivedForm
    }))));
  };

  return Form;
}(Component);
/**
 * We leverage this timeout so we can show a animate the <ProgressBar />
 * filling to 100% before unmounting the component.
 */


Form.propTypes = {
  derivedForm: array.isRequired,
  getRexValues: func.isRequired,
  pacData: pacDataPropTypes.isRequired,
  rexFormHasNoChanges: bool.isRequired,
  rexFormIsInvalid: bool.isRequired,
  rexRulesets: array.isRequired,
  rexRulesetsSayBlock: bool.isRequired,
  rexRulesetsSayPrevent: bool.isRequired,
  columnData: object,
  columnDataTitle: string
};

function postAnimationFn(fn) {
  setTimeout(fn, 666);
}

var mapState = function mapState(state, ownProps) {
  var _ownProps$pacData = ownProps.pacData,
      enableRulesets = _ownProps$pacData.enableRulesets,
      pacId = _ownProps$pacData.pacId,
      policyActionId = _ownProps$pacData.policyActionId; // When the form is removed, this component rerenders before it unmounts
  // Check to see if the pacId in in Rex before continuings

  if (!state.rex.forms[pacId]) {
    return {};
  }

  var derivedForm = deriveForm(state, {
    fusions: fusions,
    useForm: {
      id: pacId,
      childId: policyActionId
    }
  });
  var rexInputs = flattenInputs(derivedForm); // The functionality around rulesets is determined by the app invoking <Pac />
  // It they're not enabled, we essentially set all the rulesets-related data
  // that flows through the rest of the app as empty, same as if Rex told us
  // there were no rulesets to process. Therefore, the app functions as if
  // there were effects from rulesets.

  var rexRulesets = enableRulesets ? rulesetsSelector(state, pacId) : [];
  return {
    derivedForm: derivedForm,
    rexFormHasNoChanges: rexInputs.every(function (input) {
      return !!input.pristine;
    }),
    rexFormIsInvalid: rexInputs.some(function (_ref2) {
      var valid = _ref2.valid;
      return !valid && valid !== undefined;
    }),
    rexRulesets: rexRulesets,
    rexRulesetsSayBlock: enableRulesets ? Boolean(rexRulesets.find(function (ruleset) {
      return ruleset.outcome === BLOCK;
    })) : false,
    rexRulesetsSayPrevent: enableRulesets ? Boolean(rexRulesets.find(function (ruleset) {
      return ruleset.outcome === PREVENT;
    })) : false
  };
};

var createRexValuesThunk = function createRexValuesThunk(pacId, policyActionId) {
  return function (dispatch, getState) {
    var _createModelProvider = createModelProvider(getState(), {
      childId: policyActionId,
      id: pacId,
      type: "JSON"
    })("default"),
        values = _createModelProvider.values,
        changed = _createModelProvider.changed;

    return {
      values: values,
      changed: changed
    };
  };
};

var mapDispatch = function mapDispatch(dispatch, _ref3) {
  var pacData = _ref3.pacData;
  return {
    getRexValues: function getRexValues() {
      return dispatch(createRexValuesThunk(pacData.pacId, pacData.policyActionId));
    }
  };
};

export default connect(mapState, mapDispatch)(Form);